import { useState, type ReactNode } from "react";

import {
  useDeleteDocument,
  useDocumentPage,
  useEmptyTrash,
  useRestoreDocument,
  useUnarchiveDocument,
} from "../api/documents";
import { useCollections } from "../api/queries";
import type { Doc } from "../api/types";
import { granted } from "../auth/AbilityGate";
import { useAuth } from "../auth/auth-context";
import { DocumentList } from "../documents/DocumentList";
import { PageHeader } from "../layout/PageHeader";
import { formatRelativeTime } from "../lib/time";
import { Button } from "../ui/Button";
import { ConfirmDialog } from "../ui/ConfirmDialog";
import type { MenuItem } from "../ui/Menu";
import { useToast } from "../ui/toast-context";

function ScreenShell({
  title,
  description,
  actions,
  children,
}: {
  title: string;
  description: string;
  actions?: ReactNode;
  children: ReactNode;
}): ReactNode {
  return (
    <>
      <PageHeader>
        <h1 className="truncate text-base font-semibold">{title}</h1>
      </PageHeader>
      <div className="max-w-3xl">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-2xl font-semibold">{title}</h2>
            <p className="mt-2 text-sm text-muted">{description}</p>
          </div>
          {actions}
        </div>
        {children}
      </div>
    </>
  );
}

export function DraftsScreen(): ReactNode {
  const { status } = useAuth();
  const page = useDocumentPage("documents.drafts", {}, status === "authenticated");
  const collections = useCollections(status === "authenticated");
  const remove = useDeleteDocument();
  const toast = useToast();
  const [pending, setPending] = useState<Doc | null>(null);

  const rowActions = (document: Doc, abilities: Record<string, boolean>): MenuItem[] =>
    granted(abilities, "delete")
      ? [
          {
            label: "Delete",
            destructive: true,
            onSelect: () => {
              setPending(document);
            },
          },
        ]
      : [];

  return (
    <ScreenShell title="Drafts" description="Documents you have not published yet.">
      <DocumentList
        label="Drafts"
        documents={page.data?.documents ?? []}
        abilities={page.data?.abilities ?? {}}
        collections={collections.data ?? []}
        loading={page.isPending}
        error={page.error}
        emptyMessage="You have no drafts."
        rowActions={rowActions}
      />
      <ConfirmDialog
        open={pending !== null}
        title="Delete draft"
        description={`“${pending?.title ?? ""}” moves to the trash, where it can still be restored.`}
        confirmLabel="Delete"
        destructive
        busy={remove.isPending}
        onClose={() => {
          setPending(null);
        }}
        onConfirm={() => {
          if (pending === null) return;
          remove.mutate(
            { id: pending.id },
            {
              onSuccess: () => {
                toast.show("Draft moved to the trash");
                setPending(null);
              },
              onError: (error) => {
                toast.show(error.message, "error");
              },
            },
          );
        }}
      />
    </ScreenShell>
  );
}

export function ArchiveScreen(): ReactNode {
  const { status } = useAuth();
  const page = useDocumentPage("documents.archived", {}, status === "authenticated");
  const collections = useCollections(status === "authenticated");
  const unarchive = useUnarchiveDocument();
  const remove = useDeleteDocument();
  const toast = useToast();

  const rowActions = (document: Doc, abilities: Record<string, boolean>): MenuItem[] => {
    const items: MenuItem[] = [];
    if (granted(abilities, "unarchive")) {
      items.push({
        label: "Restore",
        onSelect: () => {
          unarchive.mutate(
            { id: document.id },
            {
              onSuccess: () => {
                toast.show("Document restored");
              },
              onError: (error) => {
                toast.show(error.message, "error");
              },
            },
          );
        },
      });
    }
    if (granted(abilities, "delete")) {
      items.push({
        label: "Delete",
        destructive: true,
        onSelect: () => {
          remove.mutate(
            { id: document.id },
            {
              onSuccess: () => {
                toast.show("Document moved to the trash");
              },
              onError: (error) => {
                toast.show(error.message, "error");
              },
            },
          );
        },
      });
    }
    return items;
  };

  return (
    <ScreenShell title="Archive" description="Documents archived out of the active tree.">
      <DocumentList
        label="Archived documents"
        documents={page.data?.documents ?? []}
        abilities={page.data?.abilities ?? {}}
        collections={collections.data ?? []}
        loading={page.isPending}
        error={page.error}
        emptyMessage="Nothing is archived."
        rowActions={rowActions}
        rowNote={(document) =>
          document.archivedAt === null
            ? undefined
            : `archived ${formatRelativeTime(document.archivedAt)}`
        }
      />
    </ScreenShell>
  );
}

export function TrashScreen(): ReactNode {
  const { session, status } = useAuth();
  const page = useDocumentPage("documents.deleted", {}, status === "authenticated");
  const collections = useCollections(status === "authenticated");
  const restore = useRestoreDocument();
  const remove = useDeleteDocument();
  const empty = useEmptyTrash();
  const toast = useToast();
  const [confirmEmpty, setConfirmEmpty] = useState(false);
  const [pending, setPending] = useState<Doc | null>(null);

  // `documents.empty_trash` is administrators only (`docs/spec/documents.md`),
  // and the role is the only thing the client is told about that rule.
  const canEmpty = session?.info.user.role === "admin";

  const rowActions = (document: Doc, abilities: Record<string, boolean>): MenuItem[] => {
    const items: MenuItem[] = [];
    if (granted(abilities, "restore")) {
      items.push({
        label: "Restore",
        onSelect: () => {
          restore.mutate(
            { id: document.id },
            {
              onSuccess: () => {
                toast.show("Document restored");
              },
              onError: (error) => {
                toast.show(error.message, "error");
              },
            },
          );
        },
      });
    }
    if (granted(abilities, "permanentDelete")) {
      items.push({
        label: "Delete permanently",
        destructive: true,
        onSelect: () => {
          setPending(document);
        },
      });
    }
    return items;
  };

  return (
    <ScreenShell
      title="Trash"
      description="Deleted documents, restorable until the trash is emptied."
      actions={
        canEmpty ? (
          <Button
            variant="danger"
            onClick={() => {
              setConfirmEmpty(true);
            }}
          >
            Empty trash
          </Button>
        ) : undefined
      }
    >
      <DocumentList
        label="Deleted documents"
        documents={page.data?.documents ?? []}
        abilities={page.data?.abilities ?? {}}
        collections={collections.data ?? []}
        loading={page.isPending}
        error={page.error}
        emptyMessage="The trash is empty."
        rowActions={rowActions}
        rowNote={(document) =>
          document.deletedAt === null
            ? undefined
            : `deleted ${formatRelativeTime(document.deletedAt)}`
        }
      />

      <ConfirmDialog
        open={pending !== null}
        title="Delete permanently"
        description={`“${pending?.title ?? ""}” and its revisions are removed for good. This cannot be undone.`}
        confirmLabel="Delete permanently"
        destructive
        busy={remove.isPending}
        onClose={() => {
          setPending(null);
        }}
        onConfirm={() => {
          if (pending === null) return;
          remove.mutate(
            { id: pending.id, permanent: true },
            {
              onSuccess: () => {
                toast.show("Document deleted permanently");
                setPending(null);
              },
              onError: (error) => {
                toast.show(error.message, "error");
              },
            },
          );
        }}
      />

      <ConfirmDialog
        open={confirmEmpty}
        title="Empty trash"
        description="Every deleted document in the workspace is removed for good. This cannot be undone."
        confirmLabel="Empty trash"
        destructive
        busy={empty.isPending}
        onClose={() => {
          setConfirmEmpty(false);
        }}
        onConfirm={() => {
          empty.mutate(undefined, {
            onSuccess: () => {
              toast.show("Trash emptied");
              setConfirmEmpty(false);
            },
            onError: (error) => {
              toast.show(error.message, "error");
            },
          });
        }}
      />
    </ScreenShell>
  );
}
